import React from 'react';
import { Search, ChevronDown, Plus, X, ListTree, Table } from 'lucide-react'; // Import ListTree and Table icons
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { DataType, Priority, EventSchema, FilterOptions, Parameter } from '../../types/schema';
import { ParameterViewMode } from './ParameterListContainer'; // Import ParameterViewMode

interface PropertiesHeaderProps {
  totalFilteredParametersCount: number; // New prop for total count
  filterOptions: FilterOptions;
  setFilterOptions: (options: (prev: FilterOptions) => FilterOptions) => void;
  isEditable: boolean;
  selectedEvent: EventSchema | null;
  handleAddParameter: (eventId: string, parentParam: Parameter | null, targetScope: 'global' | 'eventSpecific') => void;
  showFilterPanel: boolean;
  viewMode: ParameterViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ParameterViewMode>>;
}

const PropertiesHeader: React.FC<PropertiesHeaderProps> = ({
  totalFilteredParametersCount, // Destructure new prop
  filterOptions,
  setFilterOptions,
  isEditable,
  selectedEvent,
  handleAddParameter,
  showFilterPanel,
  viewMode,
  setViewMode,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-1">
          <div className="font-medium text-gray-900 dark:text-white">
            Properties: {totalFilteredParametersCount} {/* Use new prop for display */}
          </div>
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Filter properties by name, code, description..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm dark:placeholder-gray-400"
            value={filterOptions.searchTerm}
            onChange={(e) => setFilterOptions((prev: FilterOptions) => ({ ...prev, searchTerm: e.target.value }))}
            disabled={!isEditable}
            tabIndex={4}
          />
          {filterOptions.searchTerm && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => setFilterOptions((prev: FilterOptions) => ({ ...prev, searchTerm: '' }))}
              disabled={!isEditable}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className='flex flex-wrap gap-2'>
          {/* View Mode Toggle */}
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={clsx(
                "px-3 py-1 h-10 text-sm font-medium border-y border-l rounded-l-md",
                viewMode === 'tree'
                  ? "bg-blue-600 text-white border-blue-700"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-blue-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-blue-400/20",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => setViewMode('tree')}
              disabled={!isEditable}
              title="Tree List View"
            >
              <ListTree size={16} />
            </button>
            <button
              type="button"
              className={clsx(
                "px-3 py-1 h-10 text-sm font-medium border-y border-r rounded-r-md",
                viewMode === 'table'
                  ? "bg-blue-600 text-white border-blue-700"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-blue-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-blue-400/20",
                !isEditable && 'opacity-50 cursor-not-allowed'
              )}
              onClick={() => setViewMode('table')}
              disabled={!isEditable}
              title="Table List View"
            >
              <Table size={16} />
            </button>
          </div>
          <button
            onClick={() => selectedEvent && handleAddParameter(selectedEvent.id, null, 'eventSpecific')}
            className={clsx(
              "flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg text-white rounded-md text-sm font-medium transition-colors",
              !isEditable && 'opacity-50 cursor-not-allowed'
            )}
            disabled={!isEditable}
          >
            <Plus size={16} />
            Add Property
          </button>
        </div>
      </div>
      <AnimatePresence>
        {showFilterPanel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-4"
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showGlobal"
                checked={filterOptions.showGlobal}
                onChange={(e) => setFilterOptions((prev: FilterOptions) => ({ ...prev, showGlobal: e.target.checked }))}
                className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out dark:bg-gray-700 dark:border-gray-600 dark:checked:bg-blue-500"
              />
              <label htmlFor="showGlobal" className="text-sm text-gray-700 dark:text-gray-300">Show Global</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showEventSpecific"
                checked={filterOptions.showEventSpecific}
                onChange={(e) => setFilterOptions((prev: FilterOptions) => ({ ...prev, showEventSpecific: e.target.checked }))}
                className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out dark:bg-gray-700 dark:border-gray-600 dark:checked:bg-blue-500"
              />
              <label htmlFor="showEventSpecific" className="text-sm text-gray-700 dark:text-gray-300">Show Event-Specific</label>
            </div>
            <div className="relative w-40">
              <select
                value={filterOptions.dataType || ''}
                onChange={(e) => setFilterOptions((prev: FilterOptions) => ({ ...prev, dataType: e.target.value === '' ? null : e.target.value as DataType }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Data Types</option>
                {['string', 'number', 'boolean', 'array', 'object', 'null'].map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none" />
            </div>
            <div className="relative w-40">
              <select
                value={filterOptions.priority || ''}
                onChange={(e) => setFilterOptions((prev: FilterOptions) => ({ ...prev, priority: e.target.value === '' ? null : e.target.value as Priority }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Priorities</option>
                {['Required', 'Standard', 'Optional'].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none" />
            </div>
            <button
              onClick={() => setFilterOptions(() => ({ searchTerm: '', showGlobal: true, showEventSpecific: true, dataType: null, priority: null }))}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PropertiesHeader;
